import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "mall-my-home" }
const _hoisted_2 = {
  key: 0,
  class: "mall-my-home__todo",
  "aria-label": "처리할 일"
}
const _hoisted_3 = { class: "mall-my-home__todo-head" }
const _hoisted_4 = {
  class: "mall-my-home__summary",
  "aria-label": "쇼핑 요약"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 1,
  class: "mall-my-home__rail",
  "aria-label": "이전구매상품"
}
const _hoisted_7 = { class: "mall-my-home__rail-head" }
const _hoisted_8 = { class: "mall-my-home__rail-track" }
const _hoisted_9 = {
  key: 2,
  class: "mall-my-home__rail",
  "aria-label": "오늘 본 상품"
}
const _hoisted_10 = { class: "mall-my-home__rail-track" }
const _hoisted_11 = {
  class: "mall-my-home__menus",
  "aria-label": "바로가기"
}

import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import { ensureLiveProducts } from '@/api/mall'
import { fetchBuyAgainProducts, fetchMyOrders } from '@/api/mallOrder'
import { toMallProduct } from '@/api/mallProduct'
import { fetchMyConsultPosts } from '@/api/consultBoard'
import { fetchMyQnas, fetchMyReviews } from '@/api/mallReview'
import { useRecentlyViewed } from '@/composables/useRecentlyViewed'
import { useWishlist } from '@/composables/useWishlist'
import { MALL_ORDERS_PATH, MALL_WISHLIST_PATH } from '@/constants/mall'
import {
  MALL_MY_CLAIMS_PATH,
  MALL_MY_CONSULTS_PATH,
  MALL_MY_NAV,
  MALL_MY_REVIEWS_PATH,
} from '@/constants/mallMy'
import { getProductById, type MallProduct } from '@/constants/mallProducts'


export default /*@__PURE__*/_defineComponent({
  __name: 'MallMyHomePage',
  setup(__props) {

const { wishCount } = useWishlist()
const { recentIds } = useRecentlyViewed()

const loading = ref(true)
const orderCount = ref(0)
const claimCount = ref(0)
const reviewCount = ref(0)
const inquiryCount = ref(0)
const pendingPayCount = ref(0)
const cardPendingCount = ref(0)
const payConfirmCount = ref(0)
const confirmCount = ref(0)
const reviewableCount = ref(0)
const unansweredQna = ref(0)
const buyAgain = ref<MallProduct[]>([])

const recentProducts = computed(() =>
  recentIds.value
    .map((id) => getProductById(id))
    .filter((item): item is MallProduct => Boolean(item))
    .slice(0, 6),
)

const summaryCards = computed(() => [
  { label: '주문내역', value: orderCount.value, unit: '건', to: MALL_ORDERS_PATH },
  { label: '취소/반품', value: claimCount.value, unit: '건', to: MALL_MY_CLAIMS_PATH },
  { label: '구매후기', value: reviewCount.value, unit: '개', to: MALL_MY_REVIEWS_PATH },
  { label: '문의', value: inquiryCount.value, unit: '건', to: MALL_MY_CONSULTS_PATH },
  { label: '찜하기', value: wishCount.value, unit: '개', to: MALL_WISHLIST_PATH },
])

const todoItems = computed(() => {
  const items: Array<{
    label: string
    desc: string
    count: number
    to: string
    icon: string
  }> = []
  if (cardPendingCount.value) {
    items.push({
      label: '결제 대기 주문',
      desc: '카드·간편결제를 마쳐 주세요.',
      count: cardPendingCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-credit-card-outline',
    })
  }
  if (pendingPayCount.value) {
    items.push({
      label: '입금 대기 주문',
      desc: '계좌 이체 후 「입금했어요」를 눌러 주세요.',
      count: pendingPayCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-cash-clock',
    })
  }
  if (payConfirmCount.value) {
    items.push({
      label: '입금 확인 중',
      desc: '입금 알림을 보냈습니다. 운영팀 확인을 기다립니다.',
      count: payConfirmCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-bank-check',
    })
  }
  if (confirmCount.value) {
    items.push({
      label: '구매확정 대기',
      desc: '배송완료 상품을 확인하세요. 7일이 지나면 자동 구매확정됩니다.',
      count: confirmCount.value,
      to: MALL_ORDERS_PATH,
      icon: 'mdi-package-check',
    })
  }
  if (reviewableCount.value) {
    items.push({
      label: '작성 가능한 구매후기',
      desc: '구매확정 상품에 구매후기를 남겨 주세요.',
      count: reviewableCount.value,
      to: MALL_MY_REVIEWS_PATH,
      icon: 'mdi-message-draw',
    })
  }
  if (unansweredQna.value) {
    items.push({
      label: '답변 대기 문의',
      desc: '답변을 기다리는 문의가 있습니다.',
      count: unansweredQna.value,
      to: MALL_MY_CONSULTS_PATH,
      icon: 'mdi-forum-outline',
    })
  }
  return items
})

onMounted(async () => {
  loading.value = true
  try {
    await ensureLiveProducts()
    const [orders, reviews, qnas, consults, again] = await Promise.all([
      fetchMyOrders().catch(() => []),
      fetchMyReviews().catch(() => []),
      fetchMyQnas().catch(() => []),
      fetchMyConsultPosts({ page: 1, limit: 100, channel: 'MALL' }).catch(() => null),
      fetchBuyAgainProducts(6).catch(() => []),
    ])
    orderCount.value = orders.length
    claimCount.value = orders.filter((order) =>
      (order.itemStatuses || []).some((status) =>
        ['CANCEL_REQUEST', 'RETURN_REQUEST', 'CANCELED'].includes(status),
      ),
    ).length
    reviewCount.value = reviews.length
    inquiryCount.value = qnas.length + (consults?.pagination.total || 0)
    unansweredQna.value =
      qnas.filter((item) => !item.answer).length +
      (consults?.items.filter((item) => item.status === 'WAIT').length || 0)
    cardPendingCount.value = orders.filter(
      (order) => order.status === 'PENDING' && order.payMethod === 'TOSS',
    ).length
    pendingPayCount.value = orders.filter(
      (order) => order.status === 'PENDING' && order.payMethod !== 'TOSS' && !order.payNotifiedAt,
    ).length
    payConfirmCount.value = orders.filter(
      (order) => order.status === 'PENDING' && Boolean(order.payNotifiedAt),
    ).length

    const reviewed = new Set(reviews.map((item) => item.productCode))
    let confirm = 0
    let reviewable = 0
    orders.forEach((order) => {
      const statuses = order.itemStatuses || []
      const codes = order.itemProductCodes || []
      statuses.forEach((status, index) => {
        if (order.status === 'PAID' && status === 'DELIVERED') confirm += 1
        const productCode = codes[index]
        if (status === 'DONE' && productCode && !reviewed.has(productCode)) reviewable += 1
      })
    })
    confirmCount.value = confirm
    reviewableCount.value = reviewable
    buyAgain.value = again.map(toMallProduct)
  } finally {
    loading.value = false
  }
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[4] || (_cache[4] = _createElementVNode("header", { class: "mall-my-home__head" }, [
      _createElementVNode("h1", null, "마이페이지"),
      _createElementVNode("p", null, "주문내역 · 문의 · 구매후기 · 배송지를 관리합니다.")
    ], -1)),
    (todoItems.value.length)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[0] || (_cache[0] = _createElementVNode("h2", null, "처리할 일", -1)),
            _createElementVNode("span", null, _toDisplayString(todoItems.value.length) + "건", 1)
          ]),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(todoItems.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.to + item.label
              }, [
                _createVNode(_component_router_link, {
                  to: item.to
                }, {
                  default: _withCtx(() => [
                    _createVNode(AppIcon, {
                      icon: item.icon,
                      size: 18
                    }, null, 8, ["icon"]),
                    _createElementVNode("div", null, [
                      _createElementVNode("strong", null, _toDisplayString(item.label), 1),
                      _createElementVNode("em", null, _toDisplayString(item.desc), 1)
                    ]),
                    _createElementVNode("b", null, _toDisplayString(item.count), 1),
                    _createVNode(AppIcon, {
                      icon: "mdi-chevron-right",
                      size: 18
                    })
                  ]),
                  _: 2
                }, 1032, ["to"])
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summaryCards.value, (card) => {
        return (_openBlock(), _createBlock(_component_router_link, {
          key: card.to,
          to: card.to,
          class: "mall-my-home__card"
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(card.label), 1),
            _createElementVNode("strong", null, [
              _createTextVNode(_toDisplayString(loading.value ? '…' : card.value) + " ", 1),
              (card.unit)
                ? (_openBlock(), _createElementBlock("em", _hoisted_5, _toDisplayString(card.unit), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 2
        }, 1032, ["to"]))
      }), 128))
    ]),
    (buyAgain.value.length)
      ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[2] || (_cache[2] = _createElementVNode("h2", null, "이전구매상품", -1)),
            _createVNode(_component_router_link, { to: _unref(MALL_ORDERS_PATH) }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode("주문내역", -1)
              ]))]),
              _: 1
            }, 8, ["to"])
          ]),
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buyAgain.value, (product) => {
              return (_openBlock(), _createBlock(ProductCard, {
                key: product.id,
                product: product
              }, null, 8, ["product"]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    (recentProducts.value.length)
      ? (_openBlock(), _createElementBlock("section", _hoisted_9, [
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "mall-my-home__rail-head" }, [
            _createElementVNode("h2", null, "오늘 본 상품")
          ], -1)),
          _createElementVNode("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentProducts.value, (product) => {
              return (_openBlock(), _createBlock(ProductCard, {
                key: product.id,
                product: product
              }, null, 8, ["product"]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_11, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(MALL_MY_NAV), (group) => {
        return (_openBlock(), _createElementBlock("article", {
          key: group.title,
          class: "mall-my-home__menu"
        }, [
          _createElementVNode("h2", null, _toDisplayString(group.title), 1),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.to
              }, [
                _createVNode(_component_router_link, {
                  to: item.to
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(item.label) + " ", 1),
                    _createVNode(AppIcon, {
                      icon: "mdi-chevron-right",
                      size: 18
                    })
                  ]),
                  _: 2
                }, 1032, ["to"])
              ]))
            }), 128))
          ])
        ]))
      }), 128))
    ])
  ]))
}
}

})